import React from 'react';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CustomFieldOption } from '../../../../types/custom_fields';
import InputWithPreview from '../../global/InputWithPreview';
import { DragHandle } from '../../global/dragableTable/DragHandle';
import { DragRow } from '../../global/dragableTable/DragRow';

type SettingsCustomFieldOptionProps = {
  options: CustomFieldOption[];
  disabled?: boolean;
  updateOptions: (options: CustomFieldOption[]) => void;
};

const SettingsCustomFieldOptions: React.FC<SettingsCustomFieldOptionProps> = ({
  options,
  disabled,
  updateOptions,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option => option.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = options.findIndex(i => i.record_number === active.id);
      const overIndex = options.findIndex(i => i.record_number === over?.id);
      // update record_numbers
      // change array position
      updateOptions(
        arrayMove(options, activeIndex, overIndex).map((o, i) => ({
          ...o,
          record_number: i + 1,
        }))
      );
    }
  };

  return (
    <div>
      {/* Search input for filtering options */}
      {options.length > 0 && (
        <div className="cfs-options__search">
          <Input
            placeholder={t('settings:customFields.searchOptions')}
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            allowClear
            disabled={disabled}
          />
        </div>
      )}

      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={filteredOptions.map(i => i.record_number)}
          strategy={verticalListSortingStrategy}
        >
          <div className="cfs-options__table">
            {filteredOptions.map(option => {
              const originalIndex = options.findIndex(
                o => o.record_number === option.record_number
              );
              return (
                <DragRow
                  key={`${option.record_number}-${originalIndex}`}
                  rowId={option.record_number}
                  index={originalIndex}
                  classname="cfs-options__table__row"
                  disabled={disabled}
                >
                  <DragHandle disabled={disabled || !!searchTerm} />
                  <InputWithPreview
                    value={option.name}
                    onChange={e =>
                      updateOptions(
                        options.map((o, i) =>
                          i === originalIndex ? { ...o, name: e.target.value } : o
                        )
                      )
                    }
                  />
                  <DeleteOutlined
                    onClick={() => {
                      updateOptions(options.filter((_, i) => i !== originalIndex));
                    }}
                    style={{ marginLeft: '3px' }}
                  />
                </DragRow>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default SettingsCustomFieldOptions;
